// src/pages/ventes/ModifierClient.jsx
import React, { useState, useEffect } from "react";
import { Button, Card, Col, Row, Form, Spinner, Modal, InputGroup } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

export default function ModifierClient() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    const [clientData, setClientData] = useState({
        code_client: '',
        raison_sociale: '',
        adresse: '',
        telephone: '',
        email: '',
        numero_compte: '',
        ice: '',
        patente: '',
        rc: '',
        identifiant_fiscal: '',
        cnss: '',
        plafond_credit: 0,
        solde: 0,
        date_creation: '',
        date_modification: ''
    });

    useEffect(() => {
        fetchClientDetails();
    }, [id]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchClientDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            const result = await response.json();
            if (result.success) {
                setClientData(result.data);
            } else {
                setError("Client non trouvé");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setClientData(prev => ({
            ...prev,
            [name]: name === 'plafond_credit' || name === 'solde' ? parseFloat(value) || 0 : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!clientData.raison_sociale) {
            showToast('error', 'Champ requis', 'La raison sociale est obligatoire');
            return;
        }
        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(clientData)
                }
            );
            const result = await response.json();
            if (response.ok && result.success) {
                showToast('success', 'Client mis à jour', 'Les modifications ont été enregistrées');
                setTimeout(() => navigate('/ventes/clients/liste'), 1500);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la mise à jour");
            }
        } catch (err) {
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php?id=${id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            const result = await response.json();
            if (result.success) {
                setShowDeleteModal(false);
                showToast('success', 'Client supprimé', 'Le client a été supprimé avec succès');
                setTimeout(() => navigate('/ventes/clients/liste'), 1500);
            } else {
                showToast('error', 'Erreur', result.message);
                setShowDeleteModal(false);
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur lors de la suppression");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    /* ============================================================
       LOADING / ERROR
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-user-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement des données
                        </h5>
                        <p className="text-muted small">Récupération des informations du client...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={() => navigate('/ventes/clients/liste')} className="btn-modern-outline">
                                <i className="ri-arrow-left-line me-1"></i>Retour
                            </Button>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-pencil-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Modifier le client</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-barcode-line"></i>
                                        {clientData.code_client}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                        <i className="ri-user-line"></i>
                                        {clientData.raison_sociale}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => navigate('/ventes/clients/liste')}
                            className="btn-modern-outline"
                        >
                            <i className="ri-arrow-left-line"></i> Retour
                        </Button>
                    </div>

                    <Row className="justify-content-center">
                        <Col xl="10">

                            {/* ===== CARTE PROFIL CLIENT ===== */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4 profile-card-charte">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center gap-4 flex-wrap">
                                        <div className="client-avatar-charte">
                                            {clientData.raison_sociale?.charAt(0).toUpperCase() || 'C'}
                                        </div>
                                        <div className="flex-grow-1">
                                            <h4 className="fw-bold mb-2" style={{ color: COLORS.primaryDark }}>
                                                {clientData.raison_sociale}
                                            </h4>
                                            <div className="d-flex flex-wrap gap-3">
                                                <span className="info-chip-charte">
                                                    <i className="ri-barcode-line"></i>
                                                    Code: {clientData.code_client}
                                                </span>
                                                <span className="info-chip-charte">
                                                    <i className="ri-calendar-line"></i>
                                                    Créé le {new Date(clientData.date_creation).toLocaleDateString('fr-FR')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient">
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-edit-line me-2"></i>
                                        Modifier les informations
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form onSubmit={handleSubmit}>

                                        {/* SECTION : Informations générales */}
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                <i className="ri-information-line"></i>
                                            </span>
                                            <h6>Informations générales</h6>
                                        </div>

                                        <Row className="g-3 mb-4">
                                            <Col md="6">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Code client</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-hashtag"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="code_client"
                                                            value={clientData.code_client}
                                                            disabled
                                                            className="bg-light"
                                                        />
                                                    </InputGroup>
                                                    <Form.Text className="text-muted small">
                                                        <i className="ri-lock-line me-1"></i>Le code client ne peut pas être modifié
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md="6">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Raison sociale <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-building-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="raison_sociale"
                                                            value={clientData.raison_sociale}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md="6">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Téléphone</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-phone-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="telephone"
                                                            value={clientData.telephone}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md="6">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Email</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-mail-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            name="email"
                                                            value={clientData.email}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md="12">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Adresse</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-map-pin-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            name="adresse"
                                                            value={clientData.adresse}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* SECTION : Identifiants juridiques */}
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                                <i className="ri-briefcase-line"></i>
                                            </span>
                                            <h6>Identifiants juridiques</h6>
                                        </div>

                                        <Row className="g-3 mb-4">
                                            <Col md="6">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Numéro de compte</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-bank-card-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="numero_compte"
                                                            value={clientData.numero_compte}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md="6">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">ICE</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-fingerprint-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="ice"
                                                            value={clientData.ice}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md="6">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Patente</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-file-list-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="patente"
                                                            value={clientData.patente}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md="6">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">RC</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-book-2-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="rc"
                                                            value={clientData.rc}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md="6">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Identifiant fiscal</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-receipt-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="identifiant_fiscal"
                                                            value={clientData.identifiant_fiscal}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md="6">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">CNSS</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-shield-user-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="cnss"
                                                            value={clientData.cnss}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* SECTION : Paramètres financiers */}
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                                <i className="ri-money-euro-circle-line"></i>
                                            </span>
                                            <h6>Paramètres financiers</h6>
                                        </div>

                                        <Row className="g-3 mb-4">
                                            <Col md="6">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Plafond de crédit</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-shield-check-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            name="plafond_credit"
                                                            value={clientData.plafond_credit}
                                                            onChange={handleChange}
                                                        />
                                                        <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md="6">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Solde actuel</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-wallet-3-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            name="solde"
                                                            value={clientData.solde}
                                                            onChange={handleChange}
                                                        />
                                                        <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                                                    </InputGroup>
                                                    <Form.Text>
                                                        {clientData.solde > 0 ? (
                                                            <span className="text-danger">
                                                                <i className="ri-arrow-up-line me-1"></i>Client débiteur
                                                            </span>
                                                        ) : clientData.solde < 0 ? (
                                                            <span className="text-success">
                                                                <i className="ri-arrow-down-line me-1"></i>Client créditeur
                                                            </span>
                                                        ) : null}
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <hr className="my-4" style={{ borderColor: '#e2e8f0' }} />

                                        {/* ===== BOUTONS ===== */}
                                        <div className="d-flex justify-content-between gap-3 flex-wrap">
                                            <Button
                                                onClick={() => setShowDeleteModal(true)}
                                                disabled={saving}
                                                className="btn-modern-danger"
                                            >
                                                <i className="ri-delete-bin-line me-2"></i>
                                                Supprimer
                                            </Button>
                                            <div className="d-flex gap-3">
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => navigate('/ventes/clients/liste')}
                                                    disabled={saving}
                                                    className="btn-modern-outline"
                                                >
                                                    Annuler
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    disabled={saving}
                                                    className="btn-modern-primary"
                                                >
                                                    {saving ? (
                                                        <><Spinner size="sm" className="me-2" />Enregistrement...</>
                                                    ) : (
                                                        <><i className="ri-save-line me-2"></i>Enregistrer</>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL SUPPRESSION ===== */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-delete-bin-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Confirmer la suppression</Modal.Title>
                            <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4 text-center">
                    <p className="text-muted mb-3">
                        Êtes-vous sûr de vouloir supprimer le client <strong>{clientData.raison_sociale}</strong> ?
                    </p>
                    <div className="alert-error-charte mb-0">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                            <div className="ms-3">
                                <strong>Attention</strong>
                                <p className="mb-0 small">Toutes les données associées à ce client seront définitivement supprimées</p>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowDeleteModal(false)} className="btn-modern-outline">
                        Annuler
                    </Button>
                    <Button onClick={handleDelete} disabled={deleting} className="btn-modern-danger">
                        {deleting ? (
                            <><Spinner size="sm" className="me-2" />Suppression...</>
                        ) : (
                            <><i className="ri-delete-bin-line me-2"></i>Confirmer</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }

                /* ===== PROFIL ===== */
                .profile-card-charte {
                    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
                }
                .client-avatar-charte {
                    width: 88px; height: 88px; border-radius: 20px;
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-size: 2.2rem; font-weight: 800;
                    box-shadow: 0 8px 24px rgba(41, 90, 167, 0.3);
                    flex-shrink: 0;
                }
                .info-chip-charte {
                    display: inline-flex; align-items: center; gap: 6px;
                    background: #f1f5f9; color: #475569;
                    padding: 0.35rem 0.9rem; border-radius: 50px;
                    font-size: 0.8rem; font-weight: 500;
                }
                .info-chip-charte i { color: var(--secondary); }

                /* ===== SECTIONS ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== INPUTS ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .input-modern .form-control:disabled,
                .input-modern .form-control.bg-light {
                    background: #f1f5f9 !important;
                    color: #94a3b8;
                }
                .input-modern .input-group-text.bg-light {
                    background: #f1f5f9 !important;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    color: #64748b; font-weight: 600; font-size: 0.85rem;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.8rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.9rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-primary:disabled,
                .btn-modern-outline:disabled,
                .btn-modern-danger:disabled { opacity: 0.6; transform: none; }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== ALERTE ERREUR ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .client-avatar-charte { width: 64px; height: 64px; font-size: 1.6rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}